import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Save user to Firestore
  const saveUserToFirestore = async (firebaseUser) => {
    try {
      const userRef = doc(db, 'users', firebaseUser.uid);
      const userSnap = await getDoc(userRef);
      
      const userData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || 'User',
        photoURL: firebaseUser.photoURL || '',
        provider: firebaseUser.providerData[0]?.providerId || 'unknown',
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (!userSnap.exists()) {
        // New user
        await setDoc(userRef, {
          ...userData,
          createdAt: serverTimestamp(),
          subscription: {
            plan: 'free',
            status: 'active',
            expiresAt: null,
            paymentGateway: null
          },
          websitesCount: 0,
          productsCount: 0,
          ordersCount: 0,
          storageUsed: 0,
          isActive: true,
          role: 'user'
        });
      } else {
        // Existing user - update last login
        await setDoc(userRef, userData, { merge: true });
      }

      return true;
    } catch (error) {
      console.error('Error saving user to Firestore:', error);
      return false;
    }
  };

  // Google Sign In
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await saveUserToFirestore(result.user);
      toast.success('Successfully signed in!');
      return result.user;
    } catch (error) {
      console.error('Google sign in error:', error);
      toast.error(error.message || 'Failed to sign in with Google');
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      // Clear admin session if exists
      sessionStorage.removeItem('adminSession');
      toast.success('Successfully logged out!');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
      throw error;
    }
  };

  // Check admin session
  const checkAdminSession = () => {
    const adminSession = sessionStorage.getItem('adminSession');
    if (adminSession) {
      try {
        const session = JSON.parse(adminSession);
        if (session.isAdmin && session.expiresAt > Date.now()) {
          setIsAdmin(true);
          return true;
        } else {
          sessionStorage.removeItem('adminSession');
          setIsAdmin(false);
        }
      } catch {
        sessionStorage.removeItem('adminSession');
        setIsAdmin(false);
      }
    }
    return false;
  };

  // Admin login with sessionStorage
  const adminLogin = (username, password) => {
    if (username === 'debasish' && password === 'admin') {
      const session = {
        isAdmin: true,
        username: 'debasish',
        email: 'goraidebasish67@gmail.com',
        loginAt: Date.now(),
        expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
      };
      sessionStorage.setItem('adminSession', JSON.stringify(session));
      setIsAdmin(true);
      toast.success('Admin login successful!');
      return true;
    } else {
      toast.error('Invalid admin credentials');
      return false;
    }
  };

  // Admin logout
  const adminLogout = () => {
    sessionStorage.removeItem('adminSession');
    setIsAdmin(false);
    toast.success('Admin logged out successfully');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await saveUserToFirestore(firebaseUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    // Check for existing admin session
    checkAdminSession();

    return () => unsubscribe();
  }, []);

  const value = {
    user,
    loading,
    isAdmin,
    signInWithGoogle,
    logout,
    adminLogin,
    adminLogout,
    checkAdminSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
